import { z } from "zod";

const phoneRe = /^[+()\-\s0-9]{7,20}$/;
const monthRe = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * A single worker to invite. Name is required; at least one of phone or email
 * must be present (mirrors the backend WorkerInviteItemRequest validation).
 */
export const workerInviteItemSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    phone: z
      .string()
      .trim()
      .regex(phoneRe, "Enter a valid phone number")
      .optional()
      .or(z.literal("")),
    email: z
      .string()
      .trim()
      .email("Enter a valid email address")
      .max(255)
      .optional()
      .or(z.literal("")),
    /** Role keys from the canonical role list (constants/roles); a worker may hold several. */
    roles: z.array(z.string().min(1).max(64)).max(20).optional(),
    /** "YYYY-MM" months the worker worked for the restaurant. */
    workedFrom: z.string().regex(monthRe).optional(),
    workedTo: z.string().regex(monthRe).optional(),
    currentlyWorksHere: z.boolean().optional(),
  })
  .refine((v) => Boolean(v.phone?.trim()) || Boolean(v.email?.trim()), {
    message: "Add a phone number or an email address.",
    path: ["email"],
  })
  .refine(
    (v) =>
      v.currentlyWorksHere || !v.workedFrom || !v.workedTo || v.workedFrom <= v.workedTo,
    { message: "The end month can't be before the start month.", path: ["workedTo"] },
  );
export type WorkerInviteItem = z.infer<typeof workerInviteItemSchema>;

export const workerInvitesRequestSchema = z.object({
  workers: z.array(workerInviteItemSchema).min(1).max(100),
});
export type WorkerInvitesRequest = z.infer<typeof workerInvitesRequestSchema>;

export const workerInviteResultSchema = z.object({
  name: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  status: z.string(),
  /** Signup link the owner can share themselves; null when no invite was created. */
  inviteLink: z.string().nullish(),
});
export type WorkerInviteResult = z.infer<typeof workerInviteResultSchema>;

export const workerInvitesResponseSchema = z.object({
  message: z.string().nullish(),
  success: z.boolean(),
  invited: z.number(),
  /** Existing PublicMarket workers added straight to the roster (no invite sent). */
  addedToRoster: z.number().default(0),
  /** Emails that belong to a non-worker account (e.g. an employer); nothing was sent. */
  skippedExisting: z.number(),
  /** Phone-only workers: nothing was sent, the owner shares the invite link. */
  linkOnly: z.number().default(0),
  /** Invite emails that failed to send (the link can still be shared). */
  failed: z.number(),
  results: z.array(workerInviteResultSchema).nullish(),
});
export type WorkerInvitesResponse = z.infer<typeof workerInvitesResponseSchema>;

export const workerInvitePrefillResponseSchema = z.object({
  name: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  restaurantName: z.string().nullish(),
});
export type WorkerInvitePrefillResponse = z.infer<
  typeof workerInvitePrefillResponseSchema
>;

/** A worker the owner has already invited, as listed on Your Roster. */
export const workerInviteSummarySchema = z.object({
  id: z.number(),
  name: z.string().nullish(),
  roles: z.array(z.string()).nullish(),
  workedFrom: z.string().nullish(),
  workedTo: z.string().nullish(),
  currentlyWorksHere: z.boolean(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  status: z.string(),
  invitedAt: z.string().nullish(),
  expired: z.boolean().default(false),
  /** Signup link the owner can copy and share; null once expired. */
  inviteLink: z.string().nullish(),
});
export type WorkerInviteSummary = z.infer<typeof workerInviteSummarySchema>;

/**
 * What the inviting restaurant entered for a worker who signed up through their
 * invite link; seeds the worker onboarding Experience step.
 */
export const workerInviteExperienceSchema = z.object({
  restaurantName: z.string().nullish(),
  roles: z.array(z.string()).default([]),
  workedFrom: z.string().nullish(),
  workedTo: z.string().nullish(),
  currentlyWorksHere: z.boolean().default(false),
});
export type WorkerInviteExperience = z.infer<typeof workerInviteExperienceSchema>;
